import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { DailyNavPreviousValuation } from 'api/models/DailyNavPreviousValuation';
import { FirmsService } from 'api/services/FirmsService';
import {
  DAILY_NAV_PREVIOUS_VALUATIONS_DEFAULT_ERROR_MESSAGE,
  DAILY_NAV_PREVIOUS_VALUATIONS_QUERY_KEY,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import {
  GetDailyNavPreviousValuationsReturn,
  UseGetDailyNavPreviousValuationsParams,
} from './useGetDailyNavPreviousValuations.types';

const getDailyNavPreviousValuations = async (
  firmId: number,
  date?: string,
  fields?: string[]
): Promise<DailyNavPreviousValuation[] | null> => {
  try {
    const result = await FirmsService.apiFirmsDailyNavPreviousValuationsList(
      String(firmId),
      date,
      fields?.join(',')
    );

    if (!result) {
      return throwError(DAILY_NAV_PREVIOUS_VALUATIONS_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(DAILY_NAV_PREVIOUS_VALUATIONS_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetDailyNavPreviousValuations = (
  params: UseGetDailyNavPreviousValuationsParams
): GetDailyNavPreviousValuationsReturn => {
  const { firmId, date, fields, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetDailyNavPreviousValuationsReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getDailyNavPreviousValuations(firmId as number, date, fields),
    queryKey: [DAILY_NAV_PREVIOUS_VALUATIONS_QUERY_KEY, firmId, date ?? 'effective-date', fields?.join(',')],
    // Part of the Daily NAV page: never persist, always refetch (see useGetDailyNavRecords).
    meta: { persist: false },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetDailyNavPreviousValuations;
export { getDailyNavPreviousValuations };
